/**
 * Clockify-style weekly timesheet grid — editable.
 *
 * Rows   = unique (projectId, designationId) combinations.
 * Columns = Mon → Sun + Total.
 * Cells   = editable duration inputs that create/update TimeEntry docs.
 *
 * Mostly presentational — Firestore writes are delegated to the parent
 * via `onCellChange`. The parent also receives a `CellIndex` so it can
 * decide whether to create, update, or delete entries for each cell.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Snackbar,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import TodayOutlinedIcon from '@mui/icons-material/TodayOutlined';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import type { TimeEntry } from '@altyor-kronos/shared-types';
import { colors } from '../../core/theme/theme';
import type { WeekRange } from './timesheet';
import { formatDurationSeconds } from './timeEntries';
import TimesheetCell from './TimesheetCell';
import { dayKey, nonWorkingDay } from './workingDays';
import { useTimeLock } from './useTimeLock';
import TimeLockGuard from './TimeLockGuard';
import { formatMonthKey } from './CloseMonthDialog';

// ─── Public types ───────────────────────────────────────────────────

/** Describes the TimeEntry IDs living inside a single grid cell. */
export interface CellEntryIds {
  projectId: string;
  designationId: string;
  dayIndex: number;
  entryIds: string[];
}

export interface WeeklyBreakdownProps {
  range: WeekRange;
  entries: readonly TimeEntry[];
  totalSeconds: number;
  loading: boolean;
  error: string | null;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
  /** project id → display label (e.g. "P2059 — DiagEco") */
  projectLabels: ReadonlyMap<string, string>;
  /** designation id → display label */
  designationLabels: ReadonlyMap<string, string>;
  /**
   * Called when the user edits a cell value. The parent does the
   * Firestore write (create / update / delete) based on the cell index.
   */
  onCellChange?: (
    projectId: string,
    designationId: string,
    dayIndex: number,
    newSeconds: number,
  ) => void;
  /** Called when the user removes a row via the × button. */
  onRemoveRow?: (projectId: string, designationId: string) => void;
  /** Available projects for the "add task" picker. */
  availableProjects?: ReadonlyArray<{ id: string; label: string }>;
  /** Available designations for the "add task" picker (hybrid: existing + catalog). */
  availableDesignations?: ReadonlyArray<{ id: string; label: string; group?: string }>;
  /** Called when the user changes the project in the "add task" picker. */
  onAddRowProjectChange?: (projectId: string) => void;
  /** Called when the user confirms the "add task" picker (designationId may be encoded). */
  onAddRow?: (projectId: string, encodedDesignationId: string) => void;
  /** Phantom rows managed by the parent (resolved designation IDs). */
  phantomRows?: ReadonlyArray<{ projectId: string; designationId: string }>;
}

// ─── Grid row model ─────────────────────────────────────────────────

interface GridRow {
  projectId: string;
  designationId: string;
  /** Seconds per day (index 0 = Monday … 6 = Sunday). */
  dayCells: number[];
  /** Sum of dayCells. */
  rowTotal: number;
  /** Whether this row came from real entries (vs an "add row" phantom). */
  hasEntries: boolean;
}

function buildGridRows(entries: readonly TimeEntry[], range: WeekRange): GridRow[] {
  const map = new Map<string, GridRow>();

  for (const e of entries) {
    const key = `${e.projectId}::${e.designationId}`;
    let row = map.get(key);
    if (!row) {
      row = {
        projectId: e.projectId,
        designationId: e.designationId,
        dayCells: [0, 0, 0, 0, 0, 0, 0],
        rowTotal: 0,
        hasEntries: true,
      };
      map.set(key, row);
    }

    const ms = e.startTime.toMillis();
    const dayIndex = Math.floor((ms - range.start.getTime()) / 86400000);
    const idx = Math.max(0, Math.min(6, dayIndex));
    const secs = e.durationSeconds ?? 0;
    row.dayCells[idx] += secs;
    row.rowTotal += secs;
  }

  return Array.from(map.values()).sort((a, b) => {
    if (b.rowTotal !== a.rowTotal) return b.rowTotal - a.rowTotal;
    const ka = `${a.projectId}::${a.designationId}`;
    const kb = `${b.projectId}::${b.designationId}`;
    return ka.localeCompare(kb);
  });
}

const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

// ─── Component ──────────────────────────────────────────────────────

export default function WeeklyBreakdown(props: WeeklyBreakdownProps) {
  const { isMonthClosed, lockedThrough } = useTimeLock();
  const { t, i18n } = useTranslation();
  const {
    range,
    entries,
    totalSeconds,
    loading,
    error,
    projectLabels,
    designationLabels,
    onCellChange,
    onRemoveRow,
    availableProjects,
    availableDesignations,
    onAddRowProjectChange,
    onAddRow,
    phantomRows: parentPhantomRows,
  } = props;

  const dataRows = useMemo(() => buildGridRows(entries, range), [entries, range]);

  // Merge data rows + parent-managed phantom rows.
  const gridRows = useMemo(() => {
    const pr = parentPhantomRows ?? [];
    const dataKeys = new Set(dataRows.map((r) => `${r.projectId}::${r.designationId}`));
    const phantoms: GridRow[] = pr
      .filter((p) => !dataKeys.has(`${p.projectId}::${p.designationId}`))
      .map((p) => ({
        projectId: p.projectId,
        designationId: p.designationId,
        dayCells: [0, 0, 0, 0, 0, 0, 0],
        rowTotal: 0,
        hasEntries: false,
      }));
    return [...dataRows, ...phantoms];
  }, [dataRows, parentPhantomRows]);

  // Column totals (one per day).
  const columnTotals = useMemo(() => {
    const totals = [0, 0, 0, 0, 0, 0, 0];
    for (const row of gridRows) {
      for (let i = 0; i < 7; i++) totals[i] += row.dayCells[i];
    }
    return totals;
  }, [gridRows]);

  // Day dates for column sub-headers.
  // Columns the user explicitly opened for input on the displayed week.
  // Not persisted: moving to another week re-arms the guard.
  const [unlockedDays, setUnlockedDays] = useState<ReadonlySet<number>>(
    () => new Set(),
  );
  const [unlockHint, setUnlockHint] = useState<string | null>(null);

  /**
   * One entry per column: the actual Date, why it is off, and whether it is locked.
   *
   * `locked` et `closed` ne sont PAS la même chose : un jour non ouvré est
   * `locked` et se rouvre d'un clic, alors qu'un mois clôturé est `closed`
   * et ne se rouvre pas (seul un admin y écrit encore).
   */
  const dayStates = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const date = new Date(
        range.start.getFullYear(),
        range.start.getMonth(),
        range.start.getDate() + i,
      );
      const reason = nonWorkingDay(date);
      return {
        date,
        reason,
        locked: reason !== null && !unlockedDays.has(dayKey(date)),
        closed: isMonthClosed(date),
      };
    });
  }, [range, unlockedDays, isMonthClosed]);

  const weekHasClosedDay = dayStates.some((d) => d.closed);

  // Re-arm the guard whenever the visible week changes.
  const weekKey = dayKey(range.start);
  useEffect(() => {
    setUnlockedDays(new Set());
  }, [weekKey]);

  const unlockDay = useCallback(
    (dayIndex: number) => {
      const { date, reason } = dayStates[dayIndex];
      setUnlockedDays((prev) => new Set(prev).add(dayKey(date)));
      setUnlockHint(
        reason?.kind === 'holiday'
          ? t('kronos.timesheet.unlockedHoliday', { name: reason.name })
          : t('kronos.timesheet.unlockedWeekend'),
      );
    },
    [dayStates, t],
  );

  const dayDates = useMemo(() => {
    const locale = i18n.language;
    const fmt = new Intl.DateTimeFormat(locale, { day: '2-digit', month: 'short' });
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(
        range.start.getFullYear(),
        range.start.getMonth(),
        range.start.getDate() + i,
      );
      return fmt.format(d);
    });
  }, [range, i18n.language]);

  const headerRange = useMemo(() => {
    const locale = i18n.language;
    const fmt = new Intl.DateTimeFormat(locale, { day: '2-digit', month: 'short' });
    return t('kronos.timesheet.weekRangeLabel', {
      start: fmt.format(range.start),
      end: fmt.format(range.end),
    });
  }, [i18n.language, range, t]);

  // ── "Add task" row state ──────────────────────────────────────
  const [addingRow, setAddingRow] = useState(false);
  const [addRowProjectId, setAddRowProjectId] = useState('');
  const [addRowDesignationId, setAddRowDesignationId] = useState('');

  const handleAddRowConfirm = useCallback(() => {
    if (!addRowProjectId || !addRowDesignationId) return;
    // Delegate to parent — it resolves encoded IDs and manages phantom rows.
    onAddRow?.(addRowProjectId, addRowDesignationId);
    setAddingRow(false);
    setAddRowProjectId('');
    setAddRowDesignationId('');
  }, [addRowProjectId, addRowDesignationId, onAddRow]);

  // ── Shared cell styles ────────────────────────────────────────
  const dayCellSx = {
    textAlign: 'center' as const,
    fontVariantNumeric: 'tabular-nums',
    px: 0.5,
    minWidth: 64,
    // Keep the editable input contained: clip anything wider than the
    // fixed day column so it can never overlap the task-label cell.
    overflow: 'hidden',
  };

  return (
    <Paper elevation={0} sx={{ p: 3, border: `1px solid ${colors.lightGray}` }}>
      {/* Header bar: navigation + status */}
      <Stack
        direction={{ xs: 'column-reverse', sm: 'row' }}
        sx={{
          mb: 2,
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <IconButton
            size="small"
            aria-label={t('kronos.timesheet.prevWeek')}
            onClick={props.onPrev}
          >
            <ArrowBackIosNewIcon fontSize="small" />
          </IconButton>
          <Typography
            variant="h4"
            sx={{ fontWeight: 600, color: colors.bodyText }}
            data-testid="timesheet-week-range"
          >
            {headerRange}
          </Typography>
          <IconButton
            size="small"
            aria-label={t('kronos.timesheet.nextWeek')}
            onClick={props.onNext}
          >
            <ArrowForwardIosIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            aria-label={t('kronos.timesheet.thisWeek')}
            onClick={props.onToday}
          >
            <TodayOutlinedIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
          <Typography variant="body1" sx={{ color: colors.textMuted }}>
            {t('kronos.timesheet.total')}:{' '}
            <Box component="span" sx={{ fontWeight: 700, color: colors.bodyText }}>
              {formatDurationSeconds(totalSeconds)}
            </Box>
          </Typography>
        </Stack>
      </Stack>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {/* Les cellules closes sont grisées ; le bandeau dit POURQUOI, sinon
          l'utilisateur croit à un bug d'affichage en revenant sur août. */}
      {weekHasClosedDay && lockedThrough ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('kronos.timeLock.banner', {
            month: formatMonthKey(lockedThrough, i18n.language),
          })}
        </Alert>
      ) : null}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <>
          <TableContainer>
            <Table size="small" sx={{ tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: 'auto', minWidth: 180 }} />
                {DAY_KEYS.map((k) => (
                  <col key={k} style={{ width: 80 }} />
                ))}
                <col style={{ width: 80 }} />
              </colgroup>

              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {t('kronos.timesheet.projectColumn')}
                  </TableCell>
                  {DAY_KEYS.map((key, i) => {
                    const { reason } = dayStates[i];
                    return (
                      <TableCell
                        key={key}
                        sx={{
                          ...dayCellSx,
                          fontWeight: 700,
                          bgcolor: reason ? colors.lightGray : undefined,
                          color: reason ? colors.textMuted : undefined,
                        }}
                      >
                        <Box>{t(`kronos.timesheet.${key}`)}</Box>
                        <Typography
                          variant="caption"
                          sx={{ color: colors.textMuted, display: 'block' }}
                        >
                          {dayDates[i]}
                        </Typography>
                        {reason?.kind === 'holiday' && (
                          <Typography
                            variant="caption"
                            noWrap
                            sx={{
                              color: colors.textMuted,
                              display: 'block',
                              fontWeight: 400,
                            }}
                            title={reason.name}
                          >
                            {reason.name}
                          </Typography>
                        )}
                      </TableCell>
                    );
                  })}
                  <TableCell sx={{ ...dayCellSx, fontWeight: 700 }}>
                    {t('kronos.timesheet.total')}
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {gridRows.length === 0 && !addingRow ? (
                  <TableRow>
                    <TableCell colSpan={9} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('kronos.timesheet.noEntriesForWeek')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  gridRows.map((row) => {
                    const projLabel =
                      projectLabels.get(row.projectId) || row.projectId;
                    const desigLabel =
                      designationLabels.get(row.designationId) || row.designationId;

                    return (
                      <TableRow
                        key={`${row.projectId}::${row.designationId}`}
                        data-testid="breakdown-entry-row"
                        sx={{ '&:hover': { backgroundColor: colors.peach + '33' } }}
                      >
                        <TableCell sx={{ position: 'relative' }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: colors.bodyText }}
                            noWrap
                            title={projLabel}
                          >
                            {projLabel}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: colors.textMuted }}
                            noWrap
                            title={desigLabel}
                          >
                            {desigLabel}
                          </Typography>
                          {!row.hasEntries && (
                            <IconButton
                              size="small"
                              onClick={() => onRemoveRow?.(row.projectId, row.designationId)}
                              sx={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)' }}
                              aria-label={t('kronos.timesheet.removeRow')}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                        {row.dayCells.map((secs, dayIdx) => (
                          <TableCell
                            key={dayIdx}
                            sx={{
                              ...dayCellSx,
                              bgcolor: dayStates[dayIdx].reason
                                ? colors.lightGray
                                : undefined,
                            }}
                          >
                            <TimeLockGuard closed={dayStates[dayIdx].closed}>
                            <TimesheetCell
                              seconds={secs}
                              locked={dayStates[dayIdx].locked}
                              onUnlock={() => unlockDay(dayIdx)}
                              onSave={(newSecs) =>
                                onCellChange?.(row.projectId, row.designationId, dayIdx, newSecs)
                              }
                            />
                            </TimeLockGuard>
                          </TableCell>
                        ))}
                        <TableCell
                          sx={{
                            ...dayCellSx,
                            fontWeight: 700,
                            color: colors.bodyText,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}
                          >
                            {row.rowTotal > 0 ? formatDurationSeconds(row.rowTotal) : ''}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>

              <TableFooter>
                <TableRow
                  sx={{
                    '& td': {
                      borderTop: `2px solid ${colors.bodyText}`,
                      borderBottom: 'none',
                    },
                  }}
                >
                  <TableCell sx={{ fontWeight: 700 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {t('kronos.timesheet.weekTotal')}
                    </Typography>
                  </TableCell>
                  {columnTotals.map((secs, i) => (
                    <TableCell
                      key={i}
                      sx={{
                        ...dayCellSx,
                        fontWeight: 700,
                        color: secs > 0 ? colors.bodyText : colors.textMuted,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}
                      >
                        {secs > 0 ? formatDurationSeconds(secs) : ''}
                      </Typography>
                    </TableCell>
                  ))}
                  <TableCell sx={{ ...dayCellSx, fontWeight: 700 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}
                    >
                      {formatDurationSeconds(totalSeconds)}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>

          {/* ── "Add task" row ───────────────────────────────── */}
          {(
            <Box sx={{ mt: 1 }}>
              {addingRow ? (
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  sx={{ alignItems: { xs: 'stretch', sm: 'center' }, py: 1, flexWrap: 'wrap' }}
                >
                  <Autocomplete
                    size="small"
                    options={availableProjects ?? []}
                    getOptionLabel={(o) => o.label}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    onChange={(_, val) => {
                      setAddRowProjectId(val?.id ?? '');
                      setAddRowDesignationId('');
                      onAddRowProjectChange?.(val?.id ?? '');
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={t('kronos.timesheet.projectColumn')}
                        variant="outlined"
                        size="small"
                      />
                    )}
                    sx={{ minWidth: { xs: '100%', sm: 200 } }}
                  />
                  <Autocomplete
                    size="small"
                    options={availableDesignations ?? []}
                    getOptionLabel={(o) => o.label}
                    groupBy={(o) => o.group ?? ''}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    disabled={!addRowProjectId}
                    onChange={(_, val) => setAddRowDesignationId(val?.id ?? '')}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={t('kronos.timesheet.taskPlaceholder')}
                        variant="outlined"
                        size="small"
                      />
                    )}
                    sx={{ minWidth: { xs: '100%', sm: 260 } }}
                  />
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexShrink: 0 }}>
                    <Button
                      size="small"
                      variant="contained"
                      disabled={!addRowProjectId || !addRowDesignationId}
                      onClick={handleAddRowConfirm}
                    >
                      {t('common.ok')}
                    </Button>
                    <IconButton size="small" onClick={() => { setAddingRow(false); setAddRowProjectId(''); setAddRowDesignationId(''); }}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Stack>
              ) : (
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setAddingRow(true)}
                  sx={{ color: colors.textMuted }}
                  data-testid="add-task-button"
                >
                  {t('kronos.timesheet.addTask')}
                </Button>
              )}
            </Box>
          )}
        </>
      )}

      {/* Tells the user the column just opened and a second click will edit. */}
      <Snackbar
        open={unlockHint !== null}
        autoHideDuration={4000}
        onClose={() => setUnlockHint(null)}
        message={unlockHint}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Paper>
  );
}
